import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';
import axios from 'axios';
import Footer from '@/components/Footer';

const KNOWLEDGE_AREAS = [
  'Ciências Exatas e da Terra',
  'Ciências Biológicas',
  'Engenharias',
  'Ciências da Saúde',
  'Ciências Agrárias',
  'Ciências Sociais Aplicadas',
  'Ciências Humanas',
  'Linguística, Letras e Artes',
  'Multidisciplinar'
];

const TITULACAO_OPTIONS = [
  'Pós-Doutorado',
  'Doutorado',
  'Doutorado em Andamento',
  'Mestrado',
  'Mestrado em Andamento',
  'Especialização',
  'Especialização em Andamento',
  'Graduação',
  'Graduação em Andamento',
  'Ensino Médio',
  'Outros'
];

const GENDER_OPTIONS = [
  { value: 'Homem', label: 'Homem' },
  { value: 'Mulher', label: 'Mulher' },
  { value: 'Outro', label: 'Outro' },
  { value: 'Prefiro não informar', label: 'Prefiro Não Informar' }
];

const BRAZILIAN_STATES = [
  { uf: 'AC', name: 'Acre' },
  { uf: 'AL', name: 'Alagoas' },
  { uf: 'AP', name: 'Amapá' },
  { uf: 'AM', name: 'Amazonas' },
  { uf: 'BA', name: 'Bahia' },
  { uf: 'CE', name: 'Ceará' },
  { uf: 'DF', name: 'Distrito Federal' },
  { uf: 'ES', name: 'Espírito Santo' },
  { uf: 'GO', name: 'Goiás' },
  { uf: 'MA', name: 'Maranhão' },
  { uf: 'MT', name: 'Mato Grosso' },
  { uf: 'MS', name: 'Mato Grosso do Sul' },
  { uf: 'MG', name: 'Minas Gerais' },
  { uf: 'PA', name: 'Pará' },
  { uf: 'PB', name: 'Paraíba' },
  { uf: 'PR', name: 'Paraná' },
  { uf: 'PE', name: 'Pernambuco' },
  { uf: 'PI', name: 'Piauí' },
  { uf: 'RJ', name: 'Rio de Janeiro' },
  { uf: 'RN', name: 'Rio Grande do Norte' },
  { uf: 'RS', name: 'Rio Grande do Sul' },
  { uf: 'RO', name: 'Rondônia' },
  { uf: 'RR', name: 'Roraima' },
  { uf: 'SC', name: 'Santa Catarina' },
  { uf: 'SP', name: 'São Paulo' },
  { uf: 'SE', name: 'Sergipe' },
  { uf: 'TO', name: 'Tocantins' }
];

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Dados pessoais
    full_name: '',
    cpf: '',
    gender: '',
    birth_date: '',
    phone: '',
    
    // Dados acadêmicos
    titulacao: '',
    institution: '',
    lattes_url: '',
    google_scholar_url: '',
    knowledge_areas: [],
    
    // Áreas de interesse específicas (para rede de pareceristas)
    interesse_principal: '',
    interesse_2: '',
    interesse_3: '',
    interesse_4: '',
    
    // Endereço
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    
    // Papel no sistema
    role: 'researcher',
    
    // Preferências
    disponivel_parecerista: false,
    aceita_emails_marketing: false
  });

  // Pre-fill name from user data (e.g., from Google)
  useEffect(() => {
    if (user?.name) {
      setFormData(prev => ({ ...prev, full_name: user.name }));
    }
  }, [user]);

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const validateCPF = (cpf) => {
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length !== 11) return false;
    if (/^(\d)\1+$/.test(numbers)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers[i]) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers[9])) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers[i]) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers[10])) return false;
    
    return true;
  };

  const fetchAddressByCEP = async (cep) => {
    const numbers = cep.replace(/\D/g, '');
    if (numbers.length !== 8) return;

    setCepLoading(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${numbers}/json/`);
      if (response.data && !response.data.erro) {
        setFormData(prev => ({
          ...prev,
          logradouro: response.data.logradouro || '',
          bairro: response.data.bairro || '',
          cidade: response.data.localidade || '',
          estado: response.data.uf || ''
        }));
        toast.success('Endereço encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCEPChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, cep: formatted }));
    
    if (formatted.replace(/\D/g, '').length === 8) {
      fetchAddressByCEP(formatted);
    }
  };

  const toggleArea = (area) => {
    setFormData(prev => ({
      ...prev,
      knowledge_areas: prev.knowledge_areas.includes(area)
        ? prev.knowledge_areas.filter(a => a !== area)
        : [...prev.knowledge_areas, area]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações
    if (!formData.full_name.trim()) {
      toast.error('Nome completo é obrigatório');
      return;
    }

    if (!validateCPF(formData.cpf)) {
      toast.error('CPF inválido');
      return;
    }

    if (!formData.gender) {
      toast.error('Gênero é obrigatório');
      return;
    }

    if (!formData.birth_date) {
      toast.error('Data de nascimento é obrigatória');
      return;
    }

    if (!formData.titulacao) {
      toast.error('Titulação é obrigatória');
      return;
    }

    if (!formData.institution.trim()) {
      toast.error('Instituição é obrigatória');
      return;
    }

    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
      toast.error('Celular é obrigatório (mínimo 10 dígitos)');
      return;
    }

    if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
      toast.error('CEP é obrigatório');
      return;
    }

    if (!formData.logradouro || !formData.numero || !formData.bairro || !formData.cidade || !formData.estado) {
      toast.error('Preencha todos os campos de endereço');
      return;
    }

    if (formData.knowledge_areas.length === 0) {
      toast.error('Selecione pelo menos uma área de conhecimento');
      return;
    }

    if (!formData.interesse_principal.trim()) {
      toast.error('Área de interesse principal é obrigatória');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/complete-profile', {
        ...formData,
        cpf: formData.cpf.replace(/\D/g, ''),
        phone: formData.phone.replace(/\D/g, ''),
        cep: formData.cep.replace(/\D/g, '')
      });

      toast.success('Perfil completado com sucesso!');
      refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao completar perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <div className="flex-1 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white border border-[#E4E4E7] rounded-md p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Complete seu Cadastro</h1>
            <p className="text-[#4B4C52] mb-6">Para começar a usar o Quero Publicar, precisamos de algumas informações.</p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Seção: Dados Pessoais */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Dados Pessoais</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Nome Completo *</Label>
                    <Input
                      data-testid="full-name-input"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Seu nome completo"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>CPF *</Label>
                      <Input
                        data-testid="cpf-input"
                        value={formData.cpf}
                        onChange={(e) => setFormData({ ...formData, cpf: formatCPF(e.target.value) })}
                        placeholder="000.000.000-00"
                        maxLength={14}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Data de Nascimento *</Label>
                      <Input
                        data-testid="birth-date-input"
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Gênero *</Label>
                      <select
                        data-testid="gender-select"
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                        required
                        className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2 h-10"
                      >
                        <option value="">Selecione</option>
                        {GENDER_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Celular *</Label>
                      <Input
                        data-testid="phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Dados Acadêmicos */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Dados Acadêmicos</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Titulação *</Label>
                      <select
                        data-testid="titulacao-select"
                        value={formData.titulacao}
                        onChange={(e) => setFormData({ ...formData, titulacao: e.target.value })}
                        required
                        className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2 h-10"
                      >
                        <option value="">Selecione</option>
                        {TITULACAO_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Instituição Atual ou da Última Titulação *</Label>
                      <Input
                        data-testid="institution-input"
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                        placeholder="Nome completo (evite siglas)"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Links acadêmicos - todos opcionais */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-2">
                    <p className="text-sm text-blue-700">
                      Os links abaixo são <strong>opcionais</strong>. Você pode preencher depois no seu perfil.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Link do Currículo Lattes <span className="text-gray-400 font-normal">(opcional)</span></Label>
                      <Input
                        data-testid="lattes-input"
                        type="url"
                        value={formData.lattes_url}
                        onChange={(e) => setFormData({ ...formData, lattes_url: e.target.value })}
                        placeholder="http://lattes.cnpq.br/..."
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Link do Google Acadêmico <span className="text-gray-400 font-normal">(opcional)</span></Label>
                      <Input
                        data-testid="scholar-input"
                        type="url"
                        value={formData.google_scholar_url}
                        onChange={(e) => setFormData({ ...formData, google_scholar_url: e.target.value })}
                        placeholder="https://scholar.google.com/..."
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Áreas de Conhecimento *</Label>
                    <p className="text-sm text-[#4B4C52] mb-2">Selecione as áreas de sua atuação ou interesse</p>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 border border-[#E4E4E7] rounded-md p-4">
                      {KNOWLEDGE_AREAS.map(area => (
                        <label key={area} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.knowledge_areas.includes(area)}
                            onChange={() => toggleArea(area)}
                            className="rounded"
                          />
                          <span className="text-sm">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Áreas de Interesse Específicas */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Áreas de Interesse Específicas</h2>
                <p className="text-sm text-[#4B4C52] mb-4">
                  Informe suas áreas de interesse específicas para que possamos conectá-lo com chamadas e oportunidades relevantes.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <Label>Área de Interesse Principal *</Label>
                    <Input
                      data-testid="interesse-principal-input"
                      value={formData.interesse_principal}
                      onChange={(e) => setFormData({ ...formData, interesse_principal: e.target.value })}
                      placeholder="Ex: Sociologia Urbana, Inteligência Artificial, Saúde Coletiva..."
                      required
                      className="mt-2"
                    />
                    <p className="text-xs text-[#4B4C52] mt-1">Seja específico para melhores recomendações</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Interesse Secundário</Label>
                      <Input
                        data-testid="interesse-2-input"
                        value={formData.interesse_2}
                        onChange={(e) => setFormData({ ...formData, interesse_2: e.target.value })}
                        placeholder="Ex: Políticas Públicas"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Outro Interesse</Label>
                      <Input
                        data-testid="interesse-3-input"
                        value={formData.interesse_3}
                        onChange={(e) => setFormData({ ...formData, interesse_3: e.target.value })}
                        placeholder="Ex: Metodologia Qualitativa"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Outro Interesse</Label>
                      <Input
                        data-testid="interesse-4-input"
                        value={formData.interesse_4}
                        onChange={(e) => setFormData({ ...formData, interesse_4: e.target.value })}
                        placeholder="Ex: Estudos de Gênero"
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Endereço */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Endereço</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>CEP *</Label>
                      <div className="relative">
                        <Input
                          data-testid="cep-input"
                          value={formData.cep}
                          onChange={handleCEPChange}
                          placeholder="00000-000"
                          maxLength={9}
                          required
                          className="mt-2"
                        />
                        {cepLoading && (
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[#4B4C52]">
                            Buscando...
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <Label>Logradouro *</Label>
                      <Input
                        data-testid="logradouro-input"
                        value={formData.logradouro}
                        onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                        placeholder="Rua, Avenida..."
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <Label>Número *</Label>
                      <Input
                        data-testid="numero-input"
                        value={formData.numero}
                        onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                        placeholder="123"
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Complemento</Label>
                      <Input
                        data-testid="complemento-input"
                        value={formData.complemento}
                        onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                        placeholder="Apto, Sala..."
                        className="mt-2"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Bairro *</Label>
                      <Input
                        data-testid="bairro-input"
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                        placeholder="Bairro"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Cidade *</Label>
                      <Input
                        data-testid="cidade-input"
                        value={formData.cidade}
                        onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                        placeholder="Cidade"
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Estado *</Label>
                      <select
                        data-testid="estado-select"
                        value={formData.estado}
                        onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                        required
                        className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2 h-10"
                      >
                        <option value="">Selecione</option>
                        {BRAZILIAN_STATES.map(state => (
                          <option key={state.uf} value={state.uf}>{state.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Papel no Sistema */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Papel no Sistema</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Você é: *</Label>
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="role"
                          value="researcher"
                          checked={formData.role === 'researcher'}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="h-4 w-4"
                        />
                        <div>
                          <span className="font-medium">Pesquisador</span>
                          <p className="text-sm text-[#4B4C52]">Busco chamadas para publicar meus trabalhos</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="role"
                          value="editor"
                          checked={formData.role === 'editor'}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="h-4 w-4"
                        />
                        <div>
                          <span className="font-medium">Editor de Revista</span>
                          <p className="text-sm text-[#4B4C52]">Represento uma revista científica e divulgo chamadas</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Preferências */}
              <div>
                <h2 className="text-lg font-bold mb-4 pb-2 border-b border-[#E4E4E7]">Preferências</h2>
                
                <div className="space-y-4">
                  <label className="flex items-start gap-3 p-3 border border-[#E4E4E7] rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.disponivel_parecerista}
                      onChange={(e) => setFormData({ ...formData, disponivel_parecerista: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded"
                    />
                    <div>
                      <span className="font-medium">Disponível para ser parecerista ad hoc</span>
                      <p className="text-sm text-[#4B4C52]">
                        Estou disponível para avaliar artigos como parecerista de revistas científicas
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-[#E4E4E7] rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.aceita_emails_marketing}
                      onChange={(e) => setFormData({ ...formData, aceita_emails_marketing: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded"
                    />
                    <div>
                      <span className="font-medium">Aceito receber comunicações por email</span>
                      <p className="text-sm text-[#4B4C52]">
                        Desejo receber avisos, promoções e novidades do Quero Publicar e parceiros
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#002FA7] hover:bg-[#001f75] h-12 text-base"
                data-testid="submit-profile-btn"
              >
                {loading ? 'Salvando...' : 'Completar Cadastro'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CompleteProfilePage;
